import { unescape } from 'html-escaper';
import {
  boolean,
  coerce,
  create,
  defaulted,
  Describe,
  enums,
  number,
  optional,
  record,
  string,
  StructError,
  type,
  union,
} from 'superstruct';

import {
  Asset,
  AssetAudio,
  AssetCollection,
  AssetDocument,
  AssetIcon,
  AssetImage,
  AssetTemplateMaster,
  AssetTemplateVersion,
  AssetVideo,
  CanvaTemplate,
} from '~modules/media-library/model/Asset';
import { removeUndefined } from '~utils/object';
import stripHtml from '~utils/stripHtml';

export type AssetDto = {
  id: string;
  type:
    | 'Image'
    | 'Document'
    | 'Icon'
    | 'Audio'
    | 'Video'
    | 'MarketingSmartTemplate'
    | 'MarketingSmartTemplateVersion'
    | 'OfficeSmartTemplate'
    | 'CanvaTemplate'
    | 'Collection';
  attributes: {
    id: string;
    kaltura_id: string | undefined;
    source_id: string | undefined;
    title: string;
    short_description: string | undefined;
    filesize_formatted: string | undefined;
    fileextension: string | undefined;
    pixel_size: string | undefined;
    usage_notice: string | undefined;
    legal_notice: string | undefined;
    usage_restriction: string | undefined;
    use_information: string | undefined;
    video_preview_url: string | undefined;
    width: number | undefined;
    height: number | undefined;
    child_count: number | undefined;
    workflow_status: string | undefined;
  };
  actions: {
    copyId: boolean | undefined;
    download: boolean | undefined;
    myFiles: boolean | undefined;
    favourites: boolean | undefined;
    crop: boolean | undefined;
    web2Print: boolean | undefined;
    canva: boolean | undefined;
    edit: boolean | undefined;
    editPermissions: boolean | undefined;
    raiseRequest: boolean | undefined;
    approve: boolean | undefined;
    delete: boolean | undefined;
    restartContribution: boolean | undefined;
    jsmContactForm: boolean | undefined;
  };
  infos: {
    isFavourite: boolean | undefined;
    skipCanvaIntro: boolean | undefined;
  };
  previews: {
    list: string | undefined;
    details: string;
    zoom: string | undefined;
  };
  tabs:
    | {
        [key: string]: {
          [key: string]: string | boolean;
        };
      }
    | undefined;
};

export const AssetDtoValidator: Describe<AssetDto> = type({
  id: coerce(string(), number(), val => `${val}`),
  type: enums([
    'Image',
    'Document',
    'Icon',
    'Audio',
    'Video',
    'MarketingSmartTemplate',
    'MarketingSmartTemplateVersion',
    'OfficeSmartTemplate',
    'CanvaTemplate',
    'Collection',
  ]),
  attributes: type({
    id: string(),
    kaltura_id: optional(string()),
    source_id: optional(string()),
    title: string(),
    short_description: defaulted(string(), ''),
    filesize_formatted: optional(string()),
    fileextension: optional(string()),
    pixel_size: optional(string()),
    usage_notice: optional(string()),
    legal_notice: optional(string()),
    usage_restriction: optional(string()),
    use_information: optional(string()),
    video_preview_url: optional(string()),
    width: optional(coerce(number(), string(), val => (!!val ? parseInt(val, 10) : undefined))),
    height: optional(coerce(number(), string(), val => (!!val ? parseInt(val, 10) : undefined))),
    child_count: optional(coerce(number(), string(), val => (!!val ? parseInt(val, 10) : undefined))),
    workflow_status: optional(string()),
  }),
  actions: type({
    copyId: defaulted(boolean(), true),
    // copyId: optional(boolean()),
    download: optional(boolean()),
    myFiles: optional(boolean()),
    favourites: optional(boolean()),
    crop: optional(boolean()),
    web2Print: optional(boolean()),
    canva: optional(boolean()),
    raiseRequest: optional(boolean()),
    edit: defaulted(boolean(), false),
    jsmContactForm: optional(boolean()),
    editPermissions: defaulted(boolean(), false),
    approve: defaulted(boolean(), false),
    delete: defaulted(boolean(), false),
    restartContribution: defaulted(boolean(), false),
  }),
  infos: type({
    isFavourite: defaulted(boolean(), false),
    skipCanvaIntro: defaulted(boolean(), false),
  }),
  previews: type({
    list: optional(string()),
    details: string(),
    zoom: optional(string()),
  }),
  tabs: optional(record(string(), record(string(), union([string(), boolean()])))),
});

export function isAssetImage(asset: Asset): asset is AssetImage {
  return asset.type === 'Image';
}

export function isAssetDocument(asset: Asset): asset is AssetDocument {
  return asset.type === 'Document';
}

export function isAssetAudio(asset: Asset): asset is AssetAudio {
  return asset.type === 'Audio';
}

export function isAssetVideo(asset: Asset): asset is AssetVideo {
  return asset.type === 'Video';
}

export function isAssetIcon(asset: Asset): asset is AssetIcon {
  return asset.type === 'Icon';
}

export function isAssetCollection(asset: Asset): asset is AssetCollection {
  return asset.type === 'Collection';
}

export function isAssetTemplateMaster(asset: Asset): asset is AssetTemplateMaster {
  return asset.type === 'MarketingSmartTemplate' || asset.type === 'OfficeSmartTemplate';
}

export function isAssetTemplateVersion(asset: Asset): asset is AssetTemplateVersion {
  return asset.type === 'MarketingSmartTemplateVersion';
}

export function isCanvaTemplate(asset: Asset): asset is CanvaTemplate {
  return asset.type === 'CanvaTemplate';
}

export function makeAssetFromDto(item: AssetDto): Asset {
  return removeUndefined({
    id: `${item.id}`,
    type: item.type,
    attributes: {
      kalturaId: item.attributes.kaltura_id,
      sourceId: item.attributes.source_id,
      title: stripHtml(unescape(item.attributes.title)),
      description: unescape(item.attributes.short_description),
      filesizeFormatted: item.attributes.filesize_formatted,
      fileextension: item.attributes.fileextension,
      usageNotice: unescape(item.attributes.usage_notice || ''),
      legalNotice: unescape(item.attributes.legal_notice || ''),
      videoPreview: item.attributes.video_preview_url,
      width: item.attributes.width,
      height: item.attributes.height,
      childCount: item.attributes.child_count,
      workflowStatus: item.attributes.workflow_status,
    },
    actions: {
      copyId: item.actions.copyId,
      download: item.actions.download,
      myFiles: item.actions.myFiles,
      favourites: item.actions.favourites,
      crop: item.actions.crop,
      web2Print: item.actions.web2Print,
      canva: item.actions.canva,
      edit: item.actions.edit,
      editPermissions: item.actions.editPermissions,
      raiseRequest: item.actions.raiseRequest,
      approve: item.actions.approve,
      delete: item.actions.delete,
      restartContribution: item.actions.restartContribution,
      jsmContactForm: item.actions.jsmContactForm,
    },
    infos: {
      isFavourite: item.infos.isFavourite,
      skipCanvaIntro: item.infos.skipCanvaIntro,
    },
    previews: {
      list: item.previews.list,
      details: item.previews.details,
      zoom: item.previews.zoom,
    },
    tabs: item.tabs,
  });
}

export function makeAssetListFromDto(items: AssetDto[]) {
  return items.map(makeAssetFromDto);
}

export function makeAsset(raw: unknown) {
  try {
    const assetDto = create(raw, AssetDtoValidator);
    const asset = makeAssetFromDto(assetDto);
    return asset;
  } catch (err) {
    console.error(err);
    if (err instanceof StructError) {
      console.log('STRUCT-ERROR');
      console.log({ ...err });
    }
  }
}
